import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";

export const Select = ({
  label,
  value, // Can be: string | number | string[] | number[]
  options, // Expected: Array<{ value: string | number, label: string }>
  onChange,
  isMultiple = false,
  disabled = false,
  isNumber = false, // If true, component attempts to treat and emit values as numbers
  maxLimit = null, // Maximum number of selections for multi-select
}) => {
  const getInitialInternalValue = () => {
    if (isMultiple) {
      return Array.isArray(value) ? value : [];
    }
    return value === undefined || value === null ? "" : value;
  };

  const [internalValue, setInternalValue] = useState(getInitialInternalValue());

  useEffect(() => {
    if (isMultiple) {
      setInternalValue(Array.isArray(value) ? value : []);
    } else {
      setInternalValue(value === undefined || value === null ? "" : value);
    }
  }, [value, isMultiple]);

  const handleSelectChange = (event) => {
    const stringValueFromSelect = event.target.value; // This is always a string

    const selectedOption = options.find(
      (opt) => String(opt.value) === stringValueFromSelect
    );
    const actualSelectedValue = selectedOption ? selectedOption.value : ""; // This is the original typed value or ""

    if (isMultiple) {
      const currentArrayValues = Array.isArray(internalValue)
        ? [...internalValue]
        : [];
      let newArrayValues;

      if (currentArrayValues.some((v) => v === actualSelectedValue)) {
        newArrayValues = currentArrayValues.filter(
          (v) => v !== actualSelectedValue
        );
      } else {
        if (actualSelectedValue !== "") { // Don't add placeholder to values
          // Check max limit if set
          if (maxLimit && currentArrayValues.length >= maxLimit) {
            // Don't add if limit reached, show warning
            toast.warning(`Maximum ${maxLimit} selections allowed`);
            newArrayValues = currentArrayValues;
          } else {
            newArrayValues = [...currentArrayValues, actualSelectedValue];
          }
        } else {
          newArrayValues = currentArrayValues;
        }
      }
      setInternalValue(newArrayValues);
      onChange(newArrayValues); // Emit array of original typed values
    } else {
      // Single select
      let valueToEmit;
      if (actualSelectedValue === "") {
        valueToEmit = ""; // Placeholder selected
      } else if (isNumber) {
        const numValue = Number(actualSelectedValue);
        valueToEmit =
          isNaN(numValue) && typeof actualSelectedValue !== "number"
            ? actualSelectedValue // Emit original if NaN and original wasn't a number (e.g. "abc")
            : numValue;
      } else {
        valueToEmit = actualSelectedValue;
      }
      setInternalValue(valueToEmit);
      onChange(valueToEmit);
    }
  };

  const selectedDisplayLabels = useMemo(() => {
    if (
      !isMultiple ||
      !Array.isArray(internalValue) ||
      internalValue.length === 0 ||
      !Array.isArray(options)
    ) {
      return "";
    }
    return options
      .filter((option) => internalValue.includes(option.value))
      .map((option) => option.label)
      .join(", ");
  }, [isMultiple, internalValue, options]);

  return (
    <div className="w-full mt-3 mb-5 relative">
      {isMultiple && internalValue && internalValue.length > 0 && (
        <div
          className="text-xs mb-1 text-gray-700 dark:text-gray-300 overflow-hidden text-ellipsis whitespace-nowrap"
          title={selectedDisplayLabels}
        >
          Selected: {selectedDisplayLabels || "None"}
        </div>
      )}
      <div className="flex gap-2 relative">
        <select
          className="w-full border border-gray-300 dark:border-gray-600 p-3 rounded-full bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:ring-2 focus:ring-green-400 dark:focus:ring-green-600 cursor-pointer"
          onChange={handleSelectChange}
          disabled={disabled}
          value={
            isMultiple
              ? ""
              : String(
                  internalValue === null || internalValue === undefined
                    ? ""
                    : internalValue
                )
          }
          // If this is a custom-styled select that *looks* like a single dropdown
          // but manages multiple values, the `multiple` HTML attribute should NOT be set.
          // If it *should* be a native multi-select box, add `multiple={isMultiple}` here.
        >
          <option
            value=""
            // For single-select, placeholder is disabled.
            // For multi-select, it's enabled to allow opening the dropdown,
            // but selecting it doesn't add to values.
            disabled={!isMultiple && internalValue !== ""}
            className="cursor-pointer"
          >
            {`Select ${label}`}
          </option>

          {Array.isArray(options) &&
            options.map((option, index) => {
              const optionValue = option.value; // Original typed value
              const optionLabel = option.label;

              let isSelected;
              if (isMultiple) {
                isSelected =
                  Array.isArray(internalValue) &&
                  internalValue.includes(optionValue);
              } else {
                isSelected = internalValue === optionValue;
              }

              return (
                // HTML option value attribute must be a string
                <option key={`${String(optionValue)}-${index}`} value={String(optionValue)}>
                  {isMultiple && isSelected ? "✓ " : ""}
                  {optionLabel}
                </option>
              );
            })}
        </select>

        <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-900 dark:text-gray-100"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </div>
      </div>
    </div>
  );
};
